import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PhotoGalleryModal } from "./PhotoGalleryModal";
import { Image as ImageIcon, X, Minus, ChevronDown } from "lucide-react";
import { getObraStats } from "@/services/obraStats";

// Foto real vinda do payload dos RDOs da obra
interface GalleryPhoto {
  id: string;
  src?: string;
  caption: string;
  date: string; // yyyy-mm-dd (data do RDO)
}

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

interface PhotoGalleryProps {
  obraId?: string;
  onClose?: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
}

export function PhotoGallery({ obraId, onClose, onMinimize, isMinimized }: PhotoGalleryProps) {
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let active = true;
    if (!obraId) { setPhotos([]); return; }
    getObraStats(obraId)
      .then((s) => {
        if (!active) return;
        // RDOs já vêm ordenados do mais recente para o mais antigo
        const all: GalleryPhoto[] = [];
        for (const rdo of s.rdos) {
          (rdo.photos ?? []).forEach((p, i) => {
            // Key sempre prefixada pelo RDO: ids do payload podem repetir
            // entre RDOs diferentes (ex.: "p1" em dois relatórios).
            all.push({
              id: `${rdo.id}-${p.id ?? i}`,
              src: p.thumbnailUrl || p.url,
              caption: p.caption || "Foto do RDO",
              date: rdo.date,
            });
          });
        }
        setPhotos(all);
      })
      .catch((e) => console.error("PhotoGallery:", e));
    return () => { active = false; };
  }, [obraId]);

  const recent = photos.slice(0, 10);

  return (
    <>
      <Card className="shadow-card transition-shadow">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center justify-between w-full">
            <CardTitle className="text-lg font-semibold text-foreground">
              Fotos Recentes ({photos.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <button
                className="text-sm text-primary hover:text-primary/80 underline font-medium"
                onClick={() => setIsModalOpen(true)}
              >
                Ver todas
              </button>
              {onMinimize && (
                <button
                  onClick={onMinimize}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title={isMinimized ? "Maximizar" : "Minimizar"}
                >
                  {isMinimized ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        {!isMinimized && (
          <CardContent>
            {recent.length === 0 ? (
              <div className="py-10 flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-2">
                <ImageIcon className="h-8 w-8 opacity-40" />
                <span>Nenhuma foto ainda. As fotos dos RDOs aparecem aqui.</span>
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {recent.map((photo, index) => (
                  <div
                    key={photo.id}
                    className="group relative aspect-square bg-muted rounded-lg overflow-hidden cursor-pointer hover:shadow-card-hover transition-shadow"
                    onClick={() => setIsModalOpen(true)}
                  >
                    {photo.src ? (
                      <img
                        src={photo.src}
                        alt={photo.caption}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}

                    {index === 0 && (
                      <div className="absolute top-1 right-1 bg-primary text-primary-foreground text-xs px-1 py-0.5 rounded">
                        Novo
                      </div>
                    )}

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                    <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/60 to-transparent">
                      <p className="text-xs text-white font-medium truncate">{photo.caption}</p>
                      <p className="text-[10px] text-white/80">{formatDate(photo.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Modal de Galeria Completa */}
      <PhotoGalleryModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        obraId={obraId}
      />
    </>
  );
}
